import React from 'react';
import { Link } from 'react-router-dom';
import { EField, FieldRow } from '../sections/EditableField';

export default function BasicBlocks({ block, mode = 'view', onChange }) {
  const set = (k) => (v) => onChange?.({ [k]: v });
  switch (block.type) {
    case 'paragraph':
      return (
        <div className="ee-container py-4">
          {mode === 'edit'
            ? <EField multiline value={block.text} onChange={set('text')} mode={mode} className="text-ee-text2" />
            : <p className="text-ee-text2 leading-relaxed text-lg">{block.text}</p>}
        </div>
      );
    case 'heading': {
      const level = Math.max(1, Math.min(6, parseInt(block.level || 2, 10)));
      const Tag = `h${level}`;
      const sizes = { 1: 'text-5xl', 2: 'text-4xl', 3: 'text-3xl', 4: 'text-2xl', 5: 'text-xl', 6: 'text-lg' };
      return (
        <div className="ee-container py-4">
          {mode === 'edit' ? (
            <div className="flex gap-2 items-center">
              <select value={level} onChange={(e) => set('level')(parseInt(e.target.value, 10))} className="bg-ee-card2 border border-gold/30 rounded px-2 py-1 text-white">
                {[1,2,3,4,5,6].map(l => <option key={l} value={l}>H{l}</option>)}
              </select>
              <EField value={block.text} onChange={set('text')} mode={mode} className="font-serif text-white" />
            </div>
          ) : <Tag className={`hero-title text-white ${sizes[level]}`}>{block.text}</Tag>}
        </div>
      );
    }
    case 'list':
      return (
        <div className="ee-container py-4">
          {mode === 'edit' ? (
            <div className="space-y-1">
              {(block.items || []).map((it, i) => (
                <EField key={i} value={it} onChange={(v) => onChange?.({ items: block.items.map((x, idx) => idx === i ? v : x) })} mode={mode} />
              ))}
              <button onClick={() => onChange?.({ items: [...(block.items || []), 'New item'] })} className="text-xs text-gold mt-1">+ Item</button>
            </div>
          ) : (
            <ul className="list-disc list-inside space-y-1 text-ee-text2">
              {(block.items || []).map((it, i) => <li key={i}>{it}</li>)}
            </ul>
          )}
        </div>
      );
    case 'quote':
      return (
        <div className="ee-container py-6">
          <blockquote className="border-l-4 border-gold pl-6 italic font-serif text-2xl text-white">
            <EField multiline value={block.text} onChange={set('text')} mode={mode} />
            {(block.author || mode === 'edit') && <div className="mt-2 text-sm not-italic text-ee-muted">— <EField value={block.author} onChange={set('author')} mode={mode} /></div>}
          </blockquote>
        </div>
      );
    case 'code':
      return (
        <div className="ee-container py-4">
          {mode === 'edit'
            ? <EField multiline value={block.code} onChange={set('code')} mode={mode} className="font-mono" />
            : <pre className="bg-ee-card2 border border-ee-border rounded-lg p-4 text-sm overflow-auto text-ee-text2"><code>{block.code}</code></pre>}
        </div>
      );
    case 'image':
      return (
        <div className="ee-container py-6">
          {block.src ? <img src={block.src} alt={block.alt || ''} className="rounded-xl border border-ee-border w-full" /> : <div className="h-48 bg-ee-card rounded-xl border border-ee-border flex items-center justify-center text-ee-muted">No image</div>}
          {mode === 'edit' && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <FieldRow label="Src"><EField value={block.src} onChange={set('src')} mode={mode} /></FieldRow>
              <FieldRow label="Alt"><EField value={block.alt} onChange={set('alt')} mode={mode} /></FieldRow>
            </div>
          )}
        </div>
      );
    case 'button':
      return (
        <div className="ee-container py-4">
          {mode === 'edit' ? (
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Label"><EField value={block.label} onChange={set('label')} mode={mode} /></FieldRow>
              <FieldRow label="URL"><EField value={block.url} onChange={set('url')} mode={mode} /></FieldRow>
            </div>
          ) : (
            <Link to={block.url || '/'} className="inline-flex px-6 py-3 rounded-md gold-grad text-black font-semibold">{block.label || 'Button'}</Link>
          )}
        </div>
      );
    case 'divider': return <div className="ee-container py-4"><hr className="border-ee-border" /></div>;
    case 'spacer': return <div style={{ height: block.size || 40 }} />;
    case 'gallery':
      return (
        <div className="ee-container py-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          {(block.images || []).map((src, i) => (
            <img key={i} src={src} alt={`gallery ${i}`} className="rounded-lg border border-ee-border w-full aspect-video object-cover" />
          ))}
        </div>
      );
    case 'video':
      return (
        <div className="ee-container py-6">
          {block.src ? <video src={block.src} controls className="rounded-xl w-full" /> : <div className="h-48 bg-ee-card rounded-xl" />}
        </div>
      );
    case 'table':
      return (
        <div className="ee-container py-6 overflow-auto">
          <table className="w-full border border-ee-border text-sm text-ee-text2">
            <tbody>{(block.rows || []).map((r, i) => (
              <tr key={i} className="border-b border-ee-border">{r.map((c, j) => <td key={j} className="p-2 border-r border-ee-border">{c}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      );
    case 'columns':
      return (
        <div className="ee-container py-6 grid md:grid-cols-2 gap-6">
          {(block.columns || []).map((c, i) => <div key={i} className="text-ee-text2">{c.content || c.text}</div>)}
        </div>
      );
    default:
      return <div className="ee-container py-2 text-xs text-ee-muted">[Unknown block: {block.type}]</div>;
  }
}
